import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './authStore';
import API_CONFIG from '../config/api';

// ─── WORKFLOW PIPELINES ───────────────────────────────────────────────────────
// Do status aise hain jinke andar apne steps hote hain: review aur drawing
// submission. Pehle review ke paanch hardcoded round the ('Client Review 2'..'5')
// — har project ke liye same, badle na jaa sakne wale. Ab round status nahi hai:
// kaunse step pe hai wo task ke stage field mein hai, aur steps har project apne
// banata hai (backend: WorkflowStage + src/common/stage_kinds.py).
//
// Ye teen cheezein jodta hai: kind ka naam, wo status jise ye gate karta hai,
// aur task ka wo field jisme position rakhi jaati hai. Naya pipeline add karna
// ho to yahan ek entry aur TASK_STATUS_OPTIONS mein ek status.
export const REVIEW_STATUS = 'Client Review';
export const DRAWING_STATUS = 'Drawing Submission';

// Board ke columns ke same order mein — modal ke tab aur board ek dusre se
// alag order mein hon to padhne wale ko lagta hai do alag cheezein hain.
export const STAGE_KINDS = [
    { kind: 'drawing', status: DRAWING_STATUS, field: 'drawing_stage_id',
      labelField: 'drawing_stage_label', title: 'Drawing Submission',
      what: 'drawing submission' },
    { kind: 'review', status: REVIEW_STATUS, field: 'review_stage_id',
      labelField: 'review_stage_label', title: 'Review', what: 'review' },
];

/** Jis pipeline ka ye status hai, ya null agar kisi ka nahi. */
export const kindForStatus = (status) =>
    STAGE_KINDS.find(k => k.status === status) || null;

// ─── CLIENT STATUS AUTO-MAPPING ─────────────────────────────────────────────
// Derives client_status automatically from internal status.
// This removes the need for a separate client_status dropdown.
export const deriveClientStatus = (internalStatus) => {
    const inProgressGroup = ['Open', 'In Progress', 'Ready for Review', 'Complete', 'Completed', 'Blocker', 'Delayed'];
    if (inProgressGroup.includes(internalStatus)) return 'In Progress';
    if (internalStatus === REVIEW_STATUS) return REVIEW_STATUS;
    // Drawing submission is internal progress as far as the client is concerned
    // — the drawing has not come back to them yet.
    if (internalStatus === DRAWING_STATUS) return 'In Progress';
    if (internalStatus === 'Closed') return 'Completed';
    return 'In Progress'; // default fallback
};

// ─── PARENT TASK STATUS OPTIONS (All options) ────────────────────────────────
export const TASK_STATUS_OPTIONS = ['Open', 'In Progress', 'Ready for Review', 'Completed', 'Drawing Submission', 'Client Review', 'Closed', 'Blocker'];

// ─── SUBTASK STATUS OPTIONS ──────────────────────────────────────────────────
// Subtasks ke paas ab parent jaise hi saare options hain — koi restriction nahi.
export const SUBTASK_STATUS_OPTIONS = TASK_STATUS_OPTIONS;

// ─── STATUSES THAT CASCADE TO SUBTASKS ────────────────────────────────────────
// Cascade band hai — har task (parent ya subtask) apna status independently rakhta hai.
export const CASCADE_STATUSES = [];

// What each permission lets someone do, worded to finish "You don't have
// permission to …". Mirrors the labels on the Roles page.
const PERMISSION_ACTIONS = {
    'tasks.project.create': 'create projects',
    'tasks.project.update': 'edit this project',
    'tasks.project.delete': 'delete projects',
    'tasks.project.view_all': 'see every project in the company',
    'tasks.project.members.manage': 'add or remove project members',
    'tasks.task.create': 'create tasks or subtasks',
    'tasks.task.update': 'edit this task',
    'tasks.task.delete': 'delete tasks',
    'tasks.task.assign': 'assign tasks to other members',
    'tasks.comment.create': 'comment on tasks',
    'tasks.comment.delete': "delete other people's comments",
    'tasks.effort.log': 'log hours',
    'tasks.effort.log_backdated': 'log hours for a date more than two days back',
    'tasks.stage.manage': 'manage payment stages',
    'tasks.analytics.view': 'open Analytics and Reporting',
    'tasks.role.manage': 'manage roles',
};

const ASK_FOR_ACCESS = 'Ask your project manager or admin if you need it.';

// "You don't have permission to delete tasks. Ask …" for a permission code.
export function accessDeniedMessage(code) {
    const action = PERMISSION_ACTIONS[code];
    return action
        ? `You don't have permission to ${action}. ${ASK_FOR_ACCESS}`
        : `You don't have permission to do this. ${ASK_FOR_ACCESS}`;
}

// What to tell someone when a request fails: a refused permission in plain
// words, otherwise the server's own reason when it gave one, "no connection"
// when nothing came back at all, otherwise the caller's sentence.
export function failureMessage(err, fallback) {
    const data = err?.response?.data;
    if (err?.response?.status === 403) {
        // The server names the permission as a code ("Permission
        // 'tasks.task.delete' is required."), which means nothing to a reader.
        if (data?.error_code === 'AUTH_005') {
            const code = /'([^']+)'/.exec(data.message || '')?.[1];
            return accessDeniedMessage(code);
        }
        if (data?.error_code === 'AUTH_004') {
            return `Your role does not allow this. ${ASK_FOR_ACCESS}`;
        }
    }
    if (typeof data?.message === 'string' && data.message) return data.message;
    if (typeof data?.detail === 'string' && data.detail) return data.detail;
    if (err?.response?.status === 403) return accessDeniedMessage(null);
    // A request that got no answer; a plain Error thrown in code is not that.
    if (err?.request && !err.response) return 'Could not reach the server. Check your connection and try again.';
    return fallback;
}

export const api = axios.create({ baseURL: API_CONFIG.TASK_SERVICE });
export const api2 = axios.create({ baseURL: API_CONFIG.AUTH_SERVICE });

// Set while a 401 is being handled, so ten failing requests on one screen
// clear the session once instead of ten times.
let handlingExpiry = false;

const onSessionExpired = () => {
    if (handlingExpiry) return;
    handlingExpiry = true;

    // Imported lazily: this module is loaded while Pinia is still being set up,
    // and a top-level import of the store would run before that finishes.
    import('./authStore').then(({ useAuthStore }) => {
        try { useAuthStore().clearToken(); } catch { /* store not ready */ }
    }).catch(() => { /* fall through to the reload below */ });

    try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
    } catch { /* storage unavailable */ }

    // Full reload rather than a router push: every store still holds data
    // fetched for the expired session, and the login screen must not inherit it.
    window.location.replace('/');
};

export const setupInterceptor = (instance) => {
    instance.interceptors.request.use(config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    // A token that has expired still looks valid to the app — it is present in
    // localStorage, so isAuthenticated stays true and the dashboard renders
    // empty while every request behind it 401s. Treat the server's answer as
    // the truth and send the user back to the login screen.
    instance.interceptors.response.use(
        response => response,
        error => {
            if (error?.response?.status === 401) {
                onSessionExpired();
            }
            return Promise.reject(error);
        }
    );
};

setupInterceptor(api);
setupInterceptor(api2);

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const ASSIGNEE_PALETTE = [
    { color: 'rgba(59,130,246,0.12)', text: '#2563eb' },
    { color: 'rgba(14,165,233,0.12)', text: '#0284c7' },
    { color: 'rgba(16,185,129,0.12)', text: '#059669' },
    { color: 'rgba(245,158,11,0.12)', text: '#d97706' },
    { color: 'rgba(239,68,68,0.12)', text: '#dc2626' },
    { color: 'rgba(124,58,237,0.12)', text: '#7c3aed' }
];

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'NA';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const getAssigneeMeta = (assignedTo = '') => {
    const cleanName = assignedTo?.trim() || '';
    if (!cleanName) {
        return {
            initials: 'NA',
            name: '',
            color: 'rgba(148,163,184,0.12)',
            text: '#64748b'
        };
    }
    const hash = cleanName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const theme = ASSIGNEE_PALETTE[hash % ASSIGNEE_PALETTE.length];
    return {
        initials: getInitials(cleanName),
        name: cleanName,
        color: theme.color,
        text: theme.text
    };
};

const toIsoDate = (value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDisplayDate = (isoDate) => {
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// A standard working day. Must stay 8 — the backend derives a task's expected
// hours as duration_days × 8, and ecospace costs labour on the same basis, so a
// different number here would report hours the rest of the system disagrees with.
export const WORK_HOURS_PER_DAY = 8;

const getDurationInDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
    const diffDays = Math.floor((end - start) / DAY_IN_MS);
    return Math.max(1, diffDays + 1);
};

const normalizeDuration = (duration, startDate, endDate) => {
    const parsed = Number(duration);
    if (Number.isFinite(parsed) && parsed > 0) {
        const snapped = Math.round(parsed * 2) / 2;
        return Math.max(1, snapped);
    }
    return getDurationInDays(startDate, endDate);
};

const isTaskOverdue = (endDate, status) => {
    if (!endDate || status === 'Completed' || status === 'Closed') return false;
    const due = new Date(endDate);
    if (Number.isNaN(due.getTime())) return false;
    due.setHours(23, 59, 59, 999);
    return due.getTime() < Date.now();
};

const createTaskRecord = ({
    id,
    project_id,
    parent_task_id = null,
    title,
    description = '',
    priority = 'Medium',
    assigned_to = '',
    assigned_to_name = '',
    created_by = null,
    start_date,
    end_date,
    duration,
    status = 'Open',
    progress = 0,
    attachmentsCount = 0,
    updatesCount = 1,
    client_status = 'In Progress',
    stage_id = null,
    stage_label = null,
    review_stage_id = null,
    review_stage_label = null,
    drawing_stage_id = null,
    drawing_stage_label = null,
    parent_task_title = null
}) => {
    const normalizedStartDate = toIsoDate(start_date) || toIsoDate(new Date());
    const normalizedEndDate = toIsoDate(end_date) || normalizedStartDate;
    const normalizedDuration = normalizeDuration(duration, normalizedStartDate, normalizedEndDate);
    const normalizedDurationHours = normalizedDuration * WORK_HOURS_PER_DAY;
    const assigneeLabel = (assigned_to_name || assigned_to || '').toString().trim();
    const assignee = getAssigneeMeta(assigneeLabel);
    const normalizedProgress = Number.isFinite(Number(progress)) ? Math.max(0, Math.min(100, Number(progress))) : 0;

    return {
        id,
        project_id: String(project_id),
        parent_task_id: parent_task_id ? String(parent_task_id) : null,
        title,
        description,
        status,
        priority,
        assigned_to: assigneeLabel,
        assigned_to_id: assigned_to || null,
        assigned_to_name: assigneeLabel,
        // Carried through so the board can tell whose task this is: the server
        // lets a plain User edit a task they created as well as one assigned to
        // them, and the pencil has to agree with that.
        created_by,
        start_date: normalizedStartDate,
        end_date: normalizedEndDate,
        duration: normalizedDuration,
        duration_hours: normalizedDurationHours,
        assignee,
        startDate: formatDisplayDate(normalizedStartDate),
        dueDate: formatDisplayDate(normalizedEndDate),
        progress: normalizedProgress,
        isOverdue: isTaskOverdue(normalizedEndDate, status),
        // No comments, activity or effort logs here: the task lists do not
        // carry them. The drawer loads them for one task — see fetchTaskDetail.
        attachmentsCount,
        updatesCount,
        client_status: client_status || 'In Progress',
        // The payment stage this task counts towards. null on a manual project,
        // which has no Schedule of Payment to link to.
        stage_id: stage_id === null || stage_id === undefined ? null : Number(stage_id),
        stage_label: stage_label || null,
        // Where this task sits in each pipeline. Unrelated to stage_id above:
        // that one bills, these track who is still looking at the work. null
        // until the task first enters that pipeline's status, and kept
        // afterwards so a task in drawing submission still shows where it got
        // to in review.
        review_stage_id: review_stage_id === null || review_stage_id === undefined
            ? null : Number(review_stage_id),
        review_stage_label: review_stage_label || null,
        drawing_stage_id: drawing_stage_id === null || drawing_stage_id === undefined
            ? null : Number(drawing_stage_id),
        drawing_stage_label: drawing_stage_label || null,
        // Sent by the dashboard list, whose subtask cards name a parent that
        // may sit in a column not loaded yet.
        parent_task_title: parent_task_title || null
    };
};

// The dashboard column a stored status shows in. "Delayed" is what older rows
// hold for what the board labels "Blocker". Mirrors _column_of on the server.
export const columnOfStatus = (status) => (status === 'Delayed' ? 'Blocker' : (status || 'Open'));


export const useTaskStore = defineStore('taskStore', {
    state: () => ({
        projects: [],
        // The same projects in picker form — id, name, sector, status only.
        // Separate from `projects` so a screen that only needed a dropdown
        // cannot leave the next screen holding rows without the members and
        // progress it renders. See fetchProjectOptions.
        projectOptions: [],
        activeProjectId: null,
        // In-flight request promises, shared so that two callers asking at the
        // same moment make one request rather than two. Not rendered from —
        // they exist only to collapse same-tick duplicates.
        _profileInFlight: null,
        _projectsInFlight: null,
        _optionsInFlight: null,
        // Which task the drawer last asked about, so a late answer for a
        // task it has since left is not shown.
        _detailFor: null,
        // The board's search and status, as the server was last asked for them.
        // Held here rather than only in the component because a dozen mutations
        // in this store refetch the task list themselves — remembering the
        // filters centrally is what stops each of those from silently clearing
        // whatever the user was looking at.
        activeTaskFilters: { search: '', status: 'All' },
        // Which sector's side of a shared project the board is open on, taken
        // from the sector in the URL. A shared project is two sectors' work in
        // one project, so every read of it has to say which side is being
        // looked at — otherwise someone who can see both (an admin) gets the
        // other sector's tasks on their board. Null on an ordinary project,
        // where there is only one side. See viewer_sector_id in the backend.
        activeViewSector: null,
        // The same for the Task Dashboard's cross-project list: its project and
        // user pickers, as last sent to the server.
        activeAllTaskFilters: {},
        // Cards per dashboard column, counted by the server over everything the
        // filters match — not just the cards loaded so far.
        allTaskStatusCounts: {},
        _allTasksSeq: 0,
        tasksList: [],
        // The open drawer's history — comments, activity and effort logs for
        // one task and its subtasks — fetched when the drawer opens rather than
        // carried on every row of every task list.
        taskDetail: null,
        taskDetailLoading: false,
        // The signed-in user's hours per day on the project being looked at,
        // {'YYYY-MM-DD': hours}. Sent by the board's task list and by the
        // drawer's detail, so both log-time dialogs can say what is left of
        // the day without holding every effort log to add it up.
        myHoursByDate: {},
        // The loaded project's pipelines, keyed by kind. An empty list means
        // the project has not defined that one, and the server will refuse to
        // move anything into its status.
        stagesByKind: { review: [], drawing: [] },
        // Which project stagesByKind belongs to. Tracked separately because the
        // board reaches a project through URL slugs and never sets
        // activeProjectId, so rename/remove cannot rely on it to refetch.
        stagesProjectId: null,
        lastTaskError: '',
        companyUsers: [],
        // Paging info from the last fetchProjects call.
        projectsMeta: { total: 0, page: 1, pageSize: 200, pages: 1 },
        projectUsers: [],
        currentUser: null,
        roles: [],
        isLoading: false,
        error: null,
        // Set when a screen was refused for lack of permission; shown under the
        // header so the redirect does not look like a broken link.
        accessNotice: null,
        mockTaskMeta: JSON.parse(localStorage.getItem('mockTaskMeta') || '{}'),
        // Sector view state — Phase 11
        sectors: [],            // [{id, name, project_count, avg_progress, total_members, total_tasks}]
        emptySectors: [],       // [{id, name}] — sectors with 0 projects
        activeSectorId: null,   // currently selected sector (int) or null
    }),
    getters: {
        activeProject(state) {
            return state.projects.find(p => p.id === state.activeProjectId);
        },
        projectTasks(state) {
            return state.tasksList.filter(t => t.project_id === state.activeProjectId);
        },
        nestedTasks(state) {
            const map = {};
            const roots = [];
            state.projectTasks.forEach(t => { map[t.id] = { ...t, children: [] }; });
            state.projectTasks.forEach(t => {
                if (t.parent_task_id && map[t.parent_task_id]) {
                    map[t.parent_task_id].children.push(map[t.id]);
                } else {
                    roots.push(map[t.id]);
                }
            });
            return roots;
        },
        // Hours the signed-in user has already logged on a given date, across every
        // task in the active project. A day's work is split over several subtasks,
        // so the day's total is what matters — not any single task's.
        // `date` is a yyyy-mm-dd string, as stored on an effort log.
        // Matched on the logger's id, not their name. users.name carries no
        // UNIQUE constraint, so a second "Amit Kumar" used to have his hours
        // counted against the first one's day — the check that caps a day at
        // eight hours would fire on work the user had not done, and let real
        // overtime through unnoticed. author_id is the same column the server
        // enforces against.
        //
        // Summed by the server (my_hours_by_date) — by author id, never name.
        hoursLoggedOnDate(state) {
            return (date) => (date && Number(state.myHoursByDate?.[date])) || 0;
        },
        // What's left of the standard working day on `date` — the default offered
        // when logging time. Never negative: once the day is full the user starts
        // from 0 and types whatever overtime they actually did.
        hoursRemainingOnDate() {
            return (date) => Math.max(0, WORK_HOURS_PER_DAY - this.hoursLoggedOnDate(date));
        },
        userInitials(state) {
            const name = state.currentUser?.name;
            const email = state.currentUser?.email;

            if (name) {
                const parts = name.trim().split(/\s+/).filter(Boolean);
                if (parts.length > 0) {
                    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
                    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
                }
            }

            if (email) {
                return email.slice(0, 2).toUpperCase();
            }

            return '??';
        },
        // Delegated to authStore, which is the single source of truth for
        // identity. This getter stays so the components that already map it from
        // here keep working.
        // All three delegate to authStore, the single source of truth for
        // "who is this and what may they do?". They stay here because many
        // components already map them from this store.
        isAdmin: () => useAuthStore().isAdmin,

        // Company-wide permission check. For SHOWING things only — the API
        // enforces every one of these itself.
        can: () => (code) => useAuthStore().can(code),

        // Permission check scoped to the OPEN project, so a company-wide User
        // who is TL here sees what a TL may do. Feeds authStore the pieces it
        // cannot see: this user's membership row and the role catalogue.
        canHere: (state) => (code) => {
            const uid = String(useAuthStore().user?.id ?? state.currentUser?.id ?? '');
            const membership = state.projectUsers.find(
                pu => String(pu.user_id ?? pu.id ?? pu.userId) === uid
            );
            return useAuthStore().canIn(code, membership, state.roles);
        }
    },
    actions: {
        // The sector scope to send with a read or write on the open project,
        // as query params. Empty on an ordinary project. See activeViewSector.
        _sectorParams() {
            return this.activeViewSector ? { view_sector: String(this.activeViewSector) } : {};
        },
        saveMockMeta(taskId, meta) {
            this.mockTaskMeta[taskId] = { ...(this.mockTaskMeta[taskId] || {}), ...meta };
            localStorage.setItem('mockTaskMeta', JSON.stringify(this.mockTaskMeta));
        },
        // DEPRECATED: client_status is now derived automatically from internal status.
        // Kept for backward compatibility but no longer used in UI.
        async updateClientStatus(taskId, newStatus) {
            this.saveMockMeta(taskId, { client_status: newStatus });
            const task = this.tasksList.find(t => String(t.id) === String(taskId));
            if (task) task.client_status = newStatus;
        },
        logout() {
            this.projects = [];
            this.activeProjectId = null;
            this.tasksList = [];
            this.taskDetail = null;
            this.myHoursByDate = {};
            this.activeAllTaskFilters = {};
            this.allTaskStatusCounts = {};
            this.activeTaskFilters = { search: '', status: 'All' };
            this.projectOptions = [];
            this.companyUsers = [];
            this.projectUsers = [];
            this.currentUser = null;
            this.isLoading = false;
            this.error = null;

            const authStore = useAuthStore();
            authStore.clearToken();
        },
        // The profile, fetched at most once even when several callers ask at
        // the same moment.
        //
        // The shell and the page inside it both want it, and both mount in the
        // same tick — so a `if (!currentUser) fetch()` guard in each of them
        // loses the race every time: when the second one checks, the first
        // request is still in the air and currentUser is still null. Sharing
        // the in-flight promise is what actually makes it one request; a guard
        // on the result cannot.
        async fetchCurrentUser() {
            if (this._profileInFlight) return this._profileInFlight;

            const promise = (async () => {
                try {
                    const res = await api.get('/profile');
                    this.currentUser = res.data;
                    // Keep authStore in step: it is what the router guard and
                    // the nav read, and a stale copy there would show the wrong
                    // menu.
                    const authStore = useAuthStore();
                    authStore.setAuthData(authStore.token, res.data, res.data?.permissions || []);
                    return res.data;
                } catch (err) {
                    console.error('Failed to fetch current user', err);
                    return null;
                }
            })();

            this._profileInFlight = promise;
            // Cleared either way — a failed load can be retried, and a later
            // sign-in is not served the previous user's answer. Done after the
            // assignment above rather than in a finally inside, so a promise
            // that settles early cannot leave a stale entry behind.
            promise.finally(() => {
                if (this._profileInFlight === promise) this._profileInFlight = null;
            });
            return promise;
        },
        // `sector` stays a plain string for the callers that only ever wanted
        // one sector's projects. Everything else — search, status, sort, paging
        // — is passed as options and applied by the server, so the search box
        // finds projects that were never downloaded and a page costs one page
        // of work rather than all of it.
        //
        // The default page_size is deliberately large: several screens
        // (Reporting, Analytics, the board) still read `projects` as the whole
        // list, and quietly truncating it there would be a silent wrong answer
        // rather than a visible one. Only the project list passes a real page.
        async fetchProjects(sector = null, options = {}) {
            this.error = null;
            const params = new URLSearchParams();
            if (sector) params.set('sector', String(sector));
            if (options.search) params.set('search', options.search);
            if (options.status && options.status !== 'all') params.set('status', options.status);
            if (options.sortBy) params.set('sort_by', options.sortBy);
            if (options.sortDir) params.set('sort_dir', options.sortDir);
            params.set('page', String(options.page || 1));
            params.set('page_size', String(options.pageSize || 200));
            const qs = params.toString();

            // Shared only with a caller asking the SAME question. The dashboard
            // asks for the list twice in one tick — once itself and once from
            // inside fetchAllTasks — and both want the unfiltered list, so they
            // collapse into one request. A different sector or search is a
            // different question and gets its own.
            if (this._projectsInFlight && this._projectsInFlight.qs === qs) {
                return this._projectsInFlight.promise;
            }

            this.isLoading = true;
            const promise = (async () => {
            try {
                const res = await api.get(`/management/projects?${qs}`);
                this.projects = res.data.items || [];
                this.projectsMeta = {
                    total: res.data.total ?? this.projects.length,
                    page: res.data.page ?? 1,
                    pageSize: res.data.page_size ?? this.projects.length,
                    pages: res.data.pages ?? 1,
                    // Whole-sector totals, unaffected by search/status.
                    sectorCounts: res.data.sector_counts || null,
                };
                return this.projects;
            } catch (err) {
                this.error = failureMessage(err, 'Projects could not be loaded.');
                console.error(err);
                return [];
            } finally {
                this.isLoading = false;
            }
            })();

            this._projectsInFlight = { qs, promise };
            // Cleared after the assignment above, not inside the finally —
            // otherwise a promise that settles before this line would leave a
            // finished request recorded as still in flight.
            promise.finally(() => {
                if (this._projectsInFlight && this._projectsInFlight.promise === promise) {
                    this._projectsInFlight = null;
                }
            });
            return promise;
        },
        // The project list as a PICKER needs it: id, name, sector, status, and
        // nothing else.
        //
        // Deliberately not written into `projects`. That field is the full
        // shape — the projects list page reads members and progress off it —
        // and letting a picker overwrite it with the trimmed rows would leave
        // whichever page loaded next quietly missing fields it renders. Two
        // fields, because they hold two different things.
        //
        // `members: true` adds each project's members (id, name, email) — only
        // the Task Dashboard's user filter reads them, so no other picker pays
        // for them.
        async fetchProjectOptions({ members = false } = {}) {
            if (this._optionsInFlight && this._optionsInFlight.members === members) {
                return this._optionsInFlight.promise;
            }
            const promise = (async () => {
                try {
                    const params = { brief: true, page: 1, page_size: 200 };
                    if (members) params.members = true;
                    const res = await api.get('/management/projects', { params });
                    this.projectOptions = res.data.items || [];
                    return this.projectOptions;
                } catch (err) {
                    console.error('TaskStore: Failed to fetch project options', err);
                    this.projectOptions = [];
                    return [];
                }
            })();
            this._optionsInFlight = { members, promise };
            promise.finally(() => {
                if (this._optionsInFlight && this._optionsInFlight.promise === promise) {
                    this._optionsInFlight = null;
                }
            });
            return promise;
        },
        // One project by id, put into `projects` so everything reading that
        // list — including the id-from-URL resolvers — still works. A screen
        // that opens a single project no longer fetches every project to find
        // it.
        async fetchProject(projectId) {
            this.error = null;
            try {
                const res = await api.get(`/management/projects/${projectId}`,
                    { params: this._sectorParams() });
                const project = res.data;
                const idx = this.projects.findIndex(p => String(p.id) === String(project.id));
                if (idx >= 0) this.projects.splice(idx, 1, project);
                else this.projects.push(project);
                return project;
            } catch (err) {
                console.error('Failed to fetch project', err);
                this.error = failureMessage(err, 'This project could not be loaded.');
                return null;
            }
        },
        // Fills BOTH sector lists from one request: the server derives the empty
        // ones from the same walk over this user's projects, so asking for them
        // separately made it do that walk twice for one screen.
        async fetchSectors() {
            this.error = null;
            try {
                const res = await api.get('/management/sectors');
                this.sectors = res.data.items || [];
                this.emptySectors = Array.isArray(res.data.empty) ? res.data.empty : [];
                return this.sectors;
            } catch (err) {
                console.error('Failed to fetch sectors', err);
                this.error = failureMessage(err, 'Sectors could not be loaded.');
                this.sectors = [];
                return [];
            }
        },
        setActiveSector(sectorId) {
            this.activeSectorId = sectorId == null ? null : Number(sectorId);
        },
        // The user directory.
        //
        // `search` goes to the server, and `pageSize` caps what comes back. A
        // picker passes both and gets a first page it can type against; a
        // caller that genuinely needs everyone (the board's Manage Team modal,
        // which has to offer people not yet on the project) passes neither and
        // gets the old behaviour.
        //
        // Nothing depends on this list being complete in order to name an
        // assignee: both task endpoints send assigned_to_name, and every reader
        // prefers it — the lookup here is only a fallback for a task with no
        // assignee at all, where a fuller list would not help either.
        async fetchCompanyUsers(options = {}) {
            try {
                const params = new URLSearchParams();
                if (options.search && options.search.trim()) {
                    params.set('search', options.search.trim());
                }
                params.set('page', '1');
                params.set('page_size', String(options.pageSize || 50));
                const res = await api.get(`/users?${params.toString()}`);
                const users = Array.isArray(res.data) ? res.data : (res.data.items || []);
                this.companyUsers = users.map(user => ({
                    ...user,
                    roleLabel: Array.isArray(user.roles) && user.roles.length ? user.roles[0].name : 'Member'
                }));
                return this.companyUsers;
            } catch (err) {
                console.error(err);
                this.companyUsers = [];
                return [];
            }
        },
        async fetchProjectUsers(projectId) {
            try {
                const res = await api.get(`/management/projects/${projectId}/users`,
                    { params: this._sectorParams() });
                this.projectUsers = Array.isArray(res.data) ? res.data : (res.data.items || []);
                return this.projectUsers;
            } catch (err) {
                console.error(err);
                this.projectUsers = [];
                return [];
            }
        },
        // `roleId` is a roles.id from /management/roles. Null is meaningful: it
        // means "no per-project role", so the member's company-wide role applies
        // here as well.
        // `sectorId` is the sector this person works for on the project. Null
        // means the server picks: the project's own, or the caller's own when
        // they belong to a sector the project is shared with.
        async assignUserToProject(projectId, userId, roleId = null, sectorId = null) {
            try {
                // Backend requires project_id in the body for the DTO validation
                await api.post(`/management/projects/${projectId}/users`, {
                    project_id: Number(projectId),
                    user_id: Number(userId),
                    role_id: roleId === null || roleId === '' ? null : Number(roleId),
                    sector_id: sectorId === null || sectorId === '' ? null : String(sectorId)
                });
                await this.fetchProjectUsers(projectId);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        // Save the order the board was just dragged into, for ONE group of
        // siblings: the project's top-level tasks (parentTaskId null) or one
        // parent's children. `taskIds` is that whole group.
        //
        // The caller has already moved the row in `projectTasks`, so the board
        // does not flicker; a rejection hands the error back and the caller puts
        // its own copy back.
        async reorderTasks(projectId, parentTaskId, taskIds) {
            await api.put(`/management/projects/${projectId}/tasks/order`, {
                parent_task_id: parentTaskId === null || parentTaskId === undefined
                    ? null : Number(parentTaskId),
                task_ids: taskIds.map(Number),
            }, { params: this._sectorParams() });
        },
        // Sharing a project gives another sector its own side of it. Both
        // refetch the project, because shared_sectors rides on it.
        async shareProjectWithSector(projectId, sectorId) {
            await api.post(`/management/projects/${projectId}/sectors`, { sector_id: String(sectorId) });
            await this.fetchProject(projectId);
        },
        async unshareProjectFromSector(projectId, sectorId) {
            await api.delete(`/management/projects/${projectId}/sectors/${sectorId}`);
            await this.fetchProject(projectId);
        },
        // The role catalogue, for the member-role picker and the Roles page.
        async fetchRoles() {
            try {
                const res = await api.get('/management/roles');
                this.roles = res.data?.items || [];
                return this.roles;
            } catch (err) {
                console.error('Failed to fetch roles', err);
                this.roles = [];
                return [];
            }
        },
        async updateProjectUserRole(projectUserId, roleId, projectId) {
            await api.put(`/management/project-users/${projectUserId}/role`, {
                role_id: roleId === null || roleId === '' ? null : Number(roleId)
            });
            if (projectId) await this.fetchProjectUsers(projectId);
        },
        async removeUserFromProject(projectId, userId) {
            try {
                const projectUser = this.projectUsers.find(pu =>
                    String(pu.user_id) === String(userId) ||
                    String(pu.project_user_id || pu.id) === String(userId) ||
                    String(pu.userId) === String(userId)
                );

                if (!projectUser) {
                    throw new Error('Project member not found');
                }

                const projectUserId = projectUser.project_user_id || projectUser.id;
                await api.delete(`/management/project-users/${projectUserId}`);
                await this.fetchProjectUsers(projectId);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        // `options.search` and `options.status` are server-side filters. The
        // board used to download the whole project and narrow it in the
        // browser, which meant its search could only find what had already
        // arrived; passing them through means the search covers the project.
        async fetchTasks(projectId, options = null) {
            this.error = null;
            this.isLoading = true;
            this.activeProjectId = projectId;
            // A caller that passes filters is setting them; one that passes
            // none — every post-mutation refetch inside this store — is asking
            // for the list as it currently stands, filters included.
            if (options) {
                this.activeTaskFilters = {
                    search: options.search || '',
                    status: options.status || 'All',
                };
            }
            const active = this.activeTaskFilters;
            try {
                const params = { _t: Date.now(), ...this._sectorParams() };
                if (active.search && active.search.trim()) params.search = active.search.trim();
                if (active.status && active.status !== 'All') params.status = active.status;
                const res = await api.get(`/management/projects/${projectId}/tasks`, { params });
                this.myHoursByDate = res.data.my_hours_by_date || {};
                this.tasksList = (res.data.items || []).map(t => {
                    const idString = String(t.task_row_id || t.id);
                    const mockMeta = this.mockTaskMeta[idString] || {};
                    return createTaskRecord({
                        ...t,
                        assigned_to_name: t.assigned_to_name || this.companyUsers.find(user => String(user.id) === String(t.assigned_to))?.name || t.assignee?.name || '',
                        id: idString,
                        parent_task_id: t.parent_task_id ? String(t.parent_task_id) : null,
                        project_id: String(t.project_id),
                        assigned_to: t.assigned_to,
                        client_status: mockMeta.client_status || t.client_status
                    });
                });
            } catch (err) {
                this.error = failureMessage(err, 'Tasks could not be loaded.');
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        },
        // One request for every project's tasks, rather than one request per
        // project stitched together here. `options.from`/`options.to` narrow it
        // server side, so a month's report no longer downloads every task ever
        // created to count the ones inside the month.
        //
        // Like fetchTasks, a caller that passes options is setting the filters
        // and one that passes none — every refetch after a drag, edit or time
        // log — is asking for the list as it stands, filters included. Without
        // this, dragging a card into a gated status refetched with no filter at
        // all and the dashboard filled up with every task in the company.
        async fetchAllTasks(options = null) {
            this.error = null;
            if (options) {
                // Copied: the dashboard mutates its picker arrays in place, and
                // the store must hold what was last SENT, not what is ticked now.
                this.activeAllTaskFilters = {
                    ...options,
                    projectIds: Array.isArray(options.projectIds) ? [...options.projectIds] : options.projectIds,
                    assignedTo: Array.isArray(options.assignedTo) ? [...options.assignedTo] : options.assignedTo,
                };
            }
            options = this.activeAllTaskFilters;
            const params = this._allTaskParams(options);
            if (options.perStatus) {
                // A refetch after a drag or edit keeps as many cards per column
                // as the user had loaded with "Load more", rather than snapping
                // every column back to its first page.
                const loaded = {};
                this.tasksList.forEach(t => {
                    const col = columnOfStatus(t.status);
                    loaded[col] = (loaded[col] || 0) + 1;
                });
                const most = Math.max(0, ...Object.values(loaded));
                params.set('per_status', String(Math.min(200, Math.max(options.perStatus, most))));
            }
            // Only the newest request may write: picking three filters in a row
            // fires three, and an older one arriving last would show the wrong
            // board.
            const seq = ++this._allTasksSeq;
            this.isLoading = true;
            this.activeProjectId = null;
            try {
                const res = await api.get('/management/tasks', { params });
                if (seq !== this._allTasksSeq) return;
                this.allTaskStatusCounts = res.data.status_counts || {};
                this.tasksList = (res.data.items || []).map(t => this._toTaskRecord(t));
            } catch (err) {
                if (seq !== this._allTasksSeq) return;
                this.error = failureMessage(err, 'Your tasks could not be loaded.');
                console.error(err);
            } finally {
                if (seq === this._allTasksSeq) this.isLoading = false;
            }
        },
        // The next page of ONE dashboard column, appended to what is loaded.
        async fetchMoreTasksInColumn(column, pageSize = 50) {
            const params = this._allTaskParams(this.activeAllTaskFilters);
            const have = this.tasksList.filter(t => columnOfStatus(t.status) === column);
            params.set('status', column);
            params.set('offset', String(have.length));
            params.set('limit', String(pageSize));
            const seq = this._allTasksSeq;
            try {
                const res = await api.get('/management/tasks', { params });
                // A filter change while this was in flight makes it stale.
                if (seq !== this._allTasksSeq) return;
                const known = new Set(this.tasksList.map(t => t.id));
                const more = (res.data.items || [])
                    .map(t => this._toTaskRecord(t))
                    .filter(t => !known.has(t.id));
                this.tasksList = [...this.tasksList, ...more];
                if (res.data.status_counts) this.allTaskStatusCounts = res.data.status_counts;
            } catch (err) {
                console.error('Failed to load more tasks', err);
            }
        },
        // The dashboard's filters as query parameters. 'all' is the absence of
        // a filter, so it is sent as nothing rather than as a value.
        _allTaskParams(options = {}) {
            const params = new URLSearchParams();
            if (options.projectId) params.set('project_id', String(options.projectId));
            const list = (v) => Array.isArray(v)
                ? v.filter(x => x !== 'all' && x !== null && x !== undefined).join(',')
                : v;
            const projectIds = list(options.projectIds);
            if (projectIds) params.set('project_ids', String(projectIds));
            const assignedTo = list(options.assignedTo);
            if (assignedTo) params.set('assigned_to', String(assignedTo));
            if (options.from) params.set('from_date', options.from);
            if (options.to) params.set('to_date', options.to);
            return params;
        },
        _toTaskRecord(t) {
            const idString = String(t.task_row_id || t.id);
            const mockMeta = this.mockTaskMeta[idString] || {};
            return createTaskRecord({
                ...t,
                id: idString,
                parent_task_id: t.parent_task_id ? String(t.parent_task_id) : null,
                project_id: String(t.project_id),
                client_status: mockMeta.client_status || t.client_status
            });
        },
        // A page of the user directory, searched by the server, for a picker.
        // Returned rather than stored: two pickers on two screens asking
        // different questions must not overwrite each other's answer.
        async searchUsers({ search = '', pageSize = 50, excludeProjectId = null } = {}) {
            const params = { page: 1, page_size: pageSize };
            if (search && search.trim()) params.search = search.trim();
            if (excludeProjectId) params.exclude_project_id = excludeProjectId;
            const res = await api.get('/users', { params });
            return { items: res.data.items || [], total: res.data.total ?? 0 };
        },
        // One task's history for its drawer. A response for a task the drawer
        // has since moved away from is dropped rather than shown.
        async fetchTaskDetail(taskId) {
            const id = String(taskId);
            this._detailFor = id;
            if (!this.taskDetail || String(this.taskDetail.task_row_id) !== id) {
                this.taskDetail = null;
                this.taskDetailLoading = true;
            }
            try {
                const res = await api.get(`/management/tasks/${id}`);
                if (this._detailFor !== id) return;
                this.taskDetail = res.data;
                this.myHoursByDate = res.data?.my_hours_by_date || {};
            } catch (err) {
                console.error('Failed to load task history', err);
            } finally {
                if (this._detailFor === id) this.taskDetailLoading = false;
            }
        },
        clearTaskDetail() {
            this._detailFor = null;
            this.taskDetail = null;
            this.taskDetailLoading = false;
        },
        // Re-reads the open drawer's history after a change that adds to it.
        // Nothing to do when no drawer is open.
        async refreshTaskDetail() {
            if (this._detailFor) await this.fetchTaskDetail(this._detailFor);
        },
        // A comment changes nothing a task list shows, so only the drawer's
        // history is re-read — not the whole board, as it used to be.
        // attachmentIds: files already sent with uploadAttachment, which this
        // comment claims. With any, the text may be left empty.
        async addComment(taskId, commentText, attachmentIds = []) {
            this.lastTaskError = '';
            try {
                await api.post(`/management/tasks/${taskId}/comments`, {
                    comment: commentText,
                    comment_type: 'Normal',
                    attachment_ids: attachmentIds.map(Number)
                });
                await this.refreshTaskDetail();
                return true;
            } catch (err) {
                console.error(err);
                this.lastTaskError = failureMessage(err, 'Your comment was not posted. Try again.');
                return false;
            }
        },
        // One evidence file for a comment that is still being written. Returns
        // { id, file_name, ... }; throws with a readable message on refusal.
        async uploadAttachment(taskId, file) {
            const form = new FormData();
            form.append('file', file);
            try {
                const res = await api.post(`/management/tasks/${taskId}/attachments`, form);
                return res.data;
            } catch (err) {
                throw new Error(failureMessage(err, `${file.name} could not be attached. Try again.`));
            }
        },
        // A file picked and then removed before the comment was posted.
        async discardAttachment(attachmentId) {
            try {
                await api.delete(`/management/attachments/${attachmentId}`);
            } catch (err) {
                // Harmless if it lingers: an unclaimed file is visible to nobody.
                console.error('Failed to discard attachment', err);
            }
        },
        // Fetched with the session's token (a plain link would carry none) and
        // handed to the browser as a download.
        async downloadAttachment(attachment) {
            const res = await api.get(`/management/attachments/${attachment.id}`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = attachment.file_name || 'attachment';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        async addCommentReply(taskId, commentId, replyText) {
            this.lastTaskError = '';
            try {
                await api.post(`/management/tasks/${taskId}/comments`, {
                    comment: replyText,
                    comment_type: 'Normal',
                    parent_comment_id: Number(commentId)
                });
                await this.refreshTaskDetail();
                return true;
            } catch (err) {
                console.error(err);
                this.lastTaskError = failureMessage(err, 'Your reply was not posted. Try again.');
                return false;
            }
        },
        async addProject(projectData) {
            // sector is the tender_category enum id as a string ('1'..'4')
            // — required by backend ProjectCreateDTO since the sector refactor.
            if (!projectData.sector) {
                console.error('addProject: sector is required');
                throw new Error('Sector is required to create a project');
            }
            try {
                await api.post('/management/projects', {
                    name: projectData.name,
                    description: projectData.description,
                    sector: String(projectData.sector),
                    status: 'Active'
                });
                // No refetch here. The list page reloads its own page with its
                // own search, sort and paging (a bare fetchProjects reset all
                // three), and the sector cards refetch when navigated back to.
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async addTask(taskData, refreshAfterCreate = true) {
            this.lastTaskError = '';
            try {
                const assignedId = taskData.assigned_to || null;
                const res = await api.post('/management/tasks', {
                    project_id: Number(this.activeProjectId),
                    parent_task_id: null,
                    // Required on a project with a Schedule of Payment; the server
                    // rejects the task without it (see _resolve_stage_id).
                    stage_id: taskData.stage_id ? Number(taskData.stage_id) : null,
                    title: taskData.title,
                    description: taskData.description || '',
                    priority: taskData.priority || 'Medium',
                    assigned_to: assignedId,
                    start_date: taskData.start_date || toIsoDate(new Date()),
                    end_date: taskData.end_date || toIsoDate(new Date(Date.now() + (6 * DAY_IN_MS))),
                    duration: String(taskData.duration || 1),
                    status: 'Open'
                }, { params: this._sectorParams() });
                if (res.data && res.data.task_row_id) {
                    this.saveMockMeta(String(res.data.task_row_id), {
                        client_status: 'In Progress'
                    });
                }
                if (refreshAfterCreate) {
                    if (this.activeProjectId) {
                        await this.fetchTasks(this.activeProjectId);
                    } else {
                        await this.fetchAllTasks();
                    }
                }
                return res.data.task_row_id;
            } catch (err) {
                console.error(err);
                // Kept on the store so the form can show WHY the task was refused
                // — "select a payment stage" is a fixable mistake, and returning a
                // bare null would leave the user staring at a form that did nothing.
                this.lastTaskError = failureMessage(err, 'Could not create the task. Please try again.');
                return null;
            }
        },
        /**
         * The active project's payment stages — the options the task form offers.
         * Empty on a project with no Schedule of Payment, which is how the form
         * knows not to ask for a stage at all.
         */
        async fetchPaymentStages(projectId) {
            if (!projectId) {
                this.paymentStages = [];
                return;
            }
            try {
                const res = await api.get(`/management/projects/${projectId}/payment-stages`);
                this.paymentStages = res.data?.items || [];
            } catch (err) {
                console.error('Failed to load payment stages', err);
                this.paymentStages = [];
            }
        },
        /**
         * Every pipeline of a project, in order, in one request.
         *
         * An empty list for a kind is a meaningful answer, not a failure: it
         * means the project has not defined that pipeline, and the board uses
         * that to explain why a move into its status is refused rather than
         * letting the server 400 with no context.
         */
        async fetchStages(projectId) {
            if (!projectId) {
                this.stagesByKind = { review: [], drawing: [] };
                this.stagesProjectId = null;
                return;
            }
            try {
                const res = await api.get(`/management/projects/${projectId}/stages`);
                const items = res.data?.items || {};
                this.stagesByKind = {
                    review: items.review || [],
                    drawing: items.drawing || [],
                };
                this.stagesProjectId = projectId;
            } catch (err) {
                console.error('Failed to load workflow stages', err);
                this.stagesByKind = { review: [], drawing: [] };
                this.stagesProjectId = null;
            }
        },
        async addStage(projectId, kind, label) {
            this.lastTaskError = '';
            try {
                await api.post(`/management/projects/${projectId}/stages/${kind}`, { label });
                await this.fetchStages(projectId);
                return true;
            } catch (err) {
                this.lastTaskError = failureMessage(err, 'Could not add the stage. Please try again.');
                return false;
            }
        },
        async renameStage(stageId, label) {
            this.lastTaskError = '';
            try {
                await api.put(`/management/stages/${stageId}`, { label });
                await this.fetchStages(this.stagesProjectId);
                return true;
            } catch (err) {
                this.lastTaskError = failureMessage(err, 'Could not rename the stage.');
                return false;
            }
        },
        /**
         * Delete a stage. The server refuses while any task is on it (each
         * stage carries its task_count), so a task never loses its place.
         */
        async removeStage(stageId) {
            this.lastTaskError = '';
            try {
                await api.delete(`/management/stages/${stageId}`);
                await this.fetchStages(this.stagesProjectId);
                return true;
            } catch (err) {
                this.lastTaskError = failureMessage(err, 'Could not delete the stage.');
                // The count it was refused on may be newer than the one shown.
                await this.fetchStages(this.stagesProjectId);
                return false;
            }
        },
        async addSubtask(parentTaskId, taskData, refreshAfterCreate = true) {
            this.lastTaskError = '';
            try {
                const res = await api.post('/management/tasks', {
                    project_id: Number(this.activeProjectId),
                    parent_task_id: Number(parentTaskId),
                    title: taskData.title,
                    description: taskData.description || '',
                    priority: taskData.priority || 'Medium',
                    assigned_to: taskData.assigned_to || null,
                    start_date: taskData.start_date || toIsoDate(new Date()),
                    end_date: taskData.end_date || toIsoDate(new Date(Date.now() + (6 * DAY_IN_MS))),
                    duration: String(taskData.duration || 1),
                    status: taskData.status || 'Open'
                }, { params: this._sectorParams() });
                if (refreshAfterCreate) {
                    if (this.activeProjectId) {
                        await this.fetchTasks(this.activeProjectId);
                    } else {
                        await this.fetchAllTasks();
                    }
                }
                return res.data.task_row_id;
            } catch (err) {
                console.error(err);
                // Same reason as addTask: the caller only sees null, so without
                // this the board cannot say WHY the subtask was refused.
                this.lastTaskError = failureMessage(err, 'Could not create the subtask. Please try again.');
                return null;
            }
        },
        async updateTask(taskId, updates) {
            this.lastTaskError = '';
            try {
                // Optimistic UI update for common fields
                const task = this.tasksList.find(t => String(t.id) === String(taskId));
                if (task) {
                    if (updates.status && updates.status !== task.status) {
                        this.shiftStatusCount(task.status, updates.status);
                        task.status = updates.status;
                    }
                    if (updates.title) task.title = updates.title;
                    if (updates.priority) task.priority = updates.priority;
                    if (updates.assigned_to !== undefined) {
                        const name = this.projectUsers.find(pu =>
                            String(pu.user_id) === String(updates.assigned_to))?.user_name || '';
                        task.assigned_to_id = updates.assigned_to;
                        task.assigned_to = name;
                        task.assigned_to_name = name;
                        task.assignee = getAssigneeMeta(name);
                    }
                }

                await api.put(`/management/tasks/${taskId}`, updates);

                // Final sync — and the drawer's history, since the change was
                // written to the activity log.
                await Promise.all([this.syncAllTasks(), this.refreshTaskDetail()]);
                return true;
            } catch (err) {
                console.error('Update Task Error:', err.response?.data || err);
                // The refetch below silently reverts the optimistic edit, so without
                // surfacing the reason the save just looks like it did nothing.
                this.lastTaskError = failureMessage(err, 'Could not save the changes. Please try again.');
                // Rollback if needed (for now just refetch)
                if (this.activeProjectId) await this.fetchTasks(this.activeProjectId);
                else await this.fetchAllTasks();
                return false;
            }
        },
        async deleteTask(taskId) {
            try {
                await api.delete(`/management/tasks/${taskId}`);
                if (this.activeProjectId) {
                    await this.fetchTasks(this.activeProjectId);
                } else {
                    await this.fetchAllTasks();
                }
                return { ok: true };
            } catch (err) {
                console.error(err);
                this.lastTaskError = failureMessage(err, 'The task was not deleted. Try again.');
                return { ok: false, reason: 'SERVER_ERROR' };
            }
        },
        async updateTaskStatus(taskId, newStatus) {
            // 1. Find the task
            const task = this.tasksList.find(t => String(t.id) === String(taskId));
            const oldStatus = task ? task.status : null;

            // 2. Optimistic Update — reflect immediately in UI
            //    Sirf yahi task badalta hai; subtasks apne status pe rehte hain.
            if (task) {
                task.status = newStatus;
                // Auto-derive client_status from new internal status
                task.client_status = deriveClientStatus(newStatus);
                // The dashboard's column counts come from the server and cover
                // cards not loaded yet. Left alone, the old column kept counting
                // the card that just left it — showing "1" over an empty column
                // and a "Load more (1 more)" that had nothing to load.
                this.shiftStatusCount(oldStatus, newStatus);
            }

            try {
                // 3. Persist status to API
                await api.put(`/management/tasks/${taskId}`, { status: newStatus });
                // A move INTO a gated status makes the server place the task on
                // that pipeline's first stage. Refetch so the card shows which
                // one, rather than waiting for the next full sync.
                if (kindForStatus(newStatus) && newStatus !== oldStatus) {
                    await this.syncAllTasks();
                }
                this.lastTaskError = '';
                return true;
            } catch (err) {
                console.error('Failed to update task status:', err);
                // 4. ROLLBACK on failure
                if (task && oldStatus !== null) {
                    task.status = oldStatus;
                    task.client_status = deriveClientStatus(oldStatus);
                    this.shiftStatusCount(newStatus, oldStatus);
                }
                // The rollback above puts the card back where it was, which on
                // its own looks like the drag simply did nothing. The server's
                // reason is kept so the board can say what happened — most often
                // that this project has not defined that pipeline yet.
                this.lastTaskError = failureMessage(err, 'Could not move the task. Please try again.');
                return false;
            }
        },
        // Move one card's worth of count between two statuses, for counts the
        // server has sent. 'Delayed' is shown in the Blocker column, so it is
        // counted there when the server has no key of its own for it.
        shiftStatusCount(from, to) {
            const counts = this.allTaskStatusCounts;
            if (!counts || from === to) return;
            const key = (status) => (status in counts || status !== 'Delayed' ? status : 'Blocker');
            const fromKey = key(from);
            const toKey = key(to);
            if (typeof counts[fromKey] === 'number') counts[fromKey] = Math.max(0, counts[fromKey] - 1);
            if (typeof counts[toKey] === 'number') counts[toKey] += 1;
            else if (Object.keys(counts).length) counts[toKey] = 1;
        },
        // Full data sync — call this when loading a page, NOT after every drag
        async syncAllTasks() {
            if (this.activeProjectId) {
                await this.fetchTasks(this.activeProjectId);
            } else {
                await this.fetchAllTasks();
            }
        },
        async addTimeLog(taskId, logData) {
            try {
                // Validate date restrictions
                const logDate = new Date(logData.date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                logDate.setHours(0, 0, 0, 0);

                const daysDifference = Math.floor((today - logDate) / (1000 * 60 * 60 * 24));

                // Check: No future dates allowed for anyone
                if (logDate > today) {
                    throw new Error('Future dates are not allowed');
                }

                // Anything older than the grace window needs the backdated-log
                // permission. Checked by permission, not by role name: Manager
                // and TL hold tasks.effort.log_backdated and were being blocked
                // here despite the server allowing them through.
                // (_assert_may_log_on_date in management_service.py is the
                // enforcement; this is the courtesy message.)
                if (daysDifference > 2 && !this.canHere('tasks.effort.log_backdated')) {
                    throw new Error('You can only log hours for the last 2 days. Please contact admin for older dates.');
                }

                await api.post(`/management/tasks/${taskId}/efforts`, {
                    hours: Number(logData.hours),
                    work_description: logData.description || '',
                    log_date: logData.date
                });
                // The list for the new progress figure, the drawer for the new
                // log and the day's total.
                await Promise.all([this.syncAllTasks(), this.refreshTaskDetail()]);
                return { ok: true };
            } catch (err) {
                console.error(err);
                return { ok: false, error: err.message };
            }
        },
        async fetchBurndownAnalytics(projectId) {
            try {
                const url = `/management/projects/${projectId}/burndown-analytics`;
                const res = await api.get(url);
                return res.data;
            } catch (err) {
                console.error('TaskStore: Failed to fetch burndown analytics', err);
                return null;
            }
        },
        // The Reporting screen's whole payload — KPIs, charts and tables, worked
        // out by the server. Nothing is stored in the store: the report is a
        // snapshot of one filter combination, not shared state, and caching it
        // here would only make the next filter change serve a stale answer.
        async fetchReportingSummary(params = {}) {
            const res = await api.get('/management/reporting/summary', { params });
            return res.data;
        },
    }
});
